import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import {
  type InnogyComponentRendering,
  type ScCheckboxField,
} from '@core/jss-models';
import { RichTextField } from '@sitecore-jss/sitecore-jss-angular';
import type { Item } from '@sitecore-jss/sitecore-jss/layout';
import { getDropTreeValue, getFieldValue } from '@core/jss-utils';
import { NotificationComponent } from '@content/ui/notification';

export interface NotificationComponentRendering
  extends InnogyComponentRendering {
  showComponent?: ScCheckboxField;
  message?: RichTextField;
  pagesToShow?: Item[];
}

@Component({
  selector: 'wl-notification-container',
  templateUrl: './notification-container.component.html',
  standalone: true,
  imports: [NotificationComponent],
})
export class NotificationContainerComponent {
  readonly rendering = input<NotificationComponentRendering>();

  readonly #router = inject(Router);

  readonly #showComponent = computed(() =>
    getFieldValue<boolean>(this.rendering(), 'showComponent', false)
  );

  readonly #pages = computed(() =>
    getDropTreeValue<Item[]>(this.rendering()?.fields, 'pagesToShow', [])
  );

  readonly message = computed(
    () => this.rendering()?.fields?.message as RichTextField | undefined
  );

  readonly #isEnabledOnCurrentPage = computed(() => {
    // Map over the retrieved pages to get their URLs
    // If the URL is undefined, use an empty string and remove leading/trailing slashes
    const pagePaths = this.#pages().map((page) =>
      (page.url ?? '').replace(/(^\/|\/$)/g, '')
    );

    // Parse the current URL using the router
    const urlTree = this.#router.parseUrl(this.#router.url);

    // Extract the primary route segments from the URL tree
    // If primary route segments exist, join them to form the full path
    // If no primary route segments exist, use an empty string
    const pageUrl = urlTree.root.children['primary']
      ? urlTree.root.children['primary']?.segments
          .map((segment) => segment.path)
          .join('/')
      : '';

    // Check if the constructed pageUrl exists in the array of pagePaths
    return pagePaths.some((path) => path === pageUrl);
  });

  readonly showNotification = computed(
    () =>
      this.#showComponent() &&
      this.#isEnabledOnCurrentPage() &&
      !!this.message()?.value
  );
}
